import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts/highcharts";
import HighchartsReact from "highcharts-react-official";

import exportingModule from "highcharts/modules/exporting";
import offlineExportingModule from "highcharts/modules/offline-exporting";
import exportDataModule from "highcharts/modules/export-data";

import LoadingBlock from "../LoadingBlock/LoadingBlock";
import { baseChartOptions } from "./charts.config";

function initModule(mod, H) {
  const fn = mod?.default || mod;
  if (typeof fn === "function") fn(H);
}

initModule(exportingModule, Highcharts);
initModule(offlineExportingModule, Highcharts);
initModule(exportDataModule, Highcharts);

export default function ClippingsPerDayLineChart({
  categories = [],
  values = [],
  loading = false,
  title = "Notícias por dia",
  filename = "noticias-por-dia",
  loadingLabel = "Carregando notícias por dia...",
  exportMode = false,
}) {
  const chartRef = useRef(null);

  const PURPLE = "#6D28D9";
  const PURPLE_FILL = "rgba(109, 40, 217, 0.18)";
  const TITLE_COLOR = "#614a9e";
  const TEXT = "#111827";

  const CARD_MIN_H = 360;
  const CHART_H = 320;

  const [chartKey, setChartKey] = useState(0);
  useEffect(() => {
    const onEnd = () => setChartKey((k) => k + 1);
    window.addEventListener("dashboard-export:end", onEnd);
    return () => window.removeEventListener("dashboard-export:end", onEnd);
  }, []);

  const wrapStyle = useMemo(() => {
    if (!exportMode) {
      return {
        position: "relative",
        borderRadius: 12,
        padding: 12,
        border: "none",
        background: "var(--background-graphic)",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
        overflow: "hidden",
        minHeight: CARD_MIN_H,
      };
    }

    return {
      position: "relative",
      borderRadius: 0,
      padding: 0,
      border: "none",
      background: "var(--background-graphic)",
      boxShadow: "none",
      overflow: "hidden",
      minHeight: 560,
      width: "100%",
    };
  }, [exportMode]);

  const options = useMemo(() => {
    return {
      ...baseChartOptions,

      chart: {
        type: "areaspline",
        height: exportMode ? 560 : CHART_H,
        spacing: exportMode ? [0, 0, 0, 0] : [24, 16, 16, 24],
        backgroundColor: "transparent",
        marginTop: exportMode ? 0 : 64,
        animation: !exportMode,
      },

      title: {
        text: exportMode ? null : title,
        align: "left",
        x: 0,
        y: -3,
        margin: 12,
        style: { fontSize: "14px", fontWeight: "800", color: TITLE_COLOR },
      },

      exporting: { enabled: false },

      xAxis: {
        categories,
        tickLength: 0,
        lineWidth: 0,
        labels: {
          style: {
            fontSize: exportMode ? "13px" : "12px",
            fontWeight: "700",
            color: "var(--text-color)",
          },
        },
      },

      yAxis: {
        title: { text: null },
        gridLineDashStyle: "ShortDot",
        gridLineColor: "rgba(17, 24, 39, 0.10)",
        labels: {
          style: {
            fontSize: exportMode ? "13px" : "12px",
            fontWeight: "700",
            color: "var(--text-color)",
          },
        },
      },

      legend: { enabled: false },

      tooltip: {
        useHTML: true,
        pointFormat: `<b style="font-size:13px">{point.y}</b> notícias`,
      },

      plotOptions: {
        series: {
          animation: !exportMode,
          lineWidth: exportMode ? 4 : 3,
          color: PURPLE,
          marker: {
            enabled: true,
            radius: exportMode ? 5 : 4,
            symbol: "circle",
            fillColor: "#fff",
            lineWidth: 2,
            lineColor: PURPLE,
          },
          dataLabels: {
            enabled: true,
            allowOverlap: true,
            crop: false,
            overflow: "allow",
            y: -10,
            backgroundColor: "rgba(255,255,255,0.92)",
            borderRadius: 6,
            padding: exportMode ? 5 : 3,
            style: {
              fontWeight: "900",
              fontSize: exportMode ? "17px" : "11px",
              color: TEXT,
              textOutline: "none",
            },
            formatter: function () {
              return this.y;
            },
          },
        },
        areaspline: {
          fillColor: PURPLE_FILL,
          fillOpacity: 1,
        },
      },

      series: [{ name: "Notícias", data: values }],
      credits: { enabled: false },
    };
  }, [categories, values, title, exportMode]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    const t = setTimeout(() => {
      try {
        chart.reflow();

        if (exportMode) {
          const w = chart.renderTo?.clientWidth || null;
          chart.setSize(w, 560, false);
          chart.reflow();
        }
      } catch {}
    }, exportMode ? 60 : 0);

    return () => clearTimeout(t);
  }, [exportMode, categories, values]);

  const handleExportPNG = () => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    chart.exportChart(
      { type: "image/png", filename },
      { chart: { backgroundColor: "var(--background-graphic)" } }
    );
  };

  const exportBtnStyle = {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 5,

    height: 34,
    padding: "0 12px",
    borderRadius: 12,
    border: "none",
    background: "var(--bottom-dropdown-color)",
    color: "var(--text-color)",
    fontWeight: 900,
    fontSize: 12,

    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    userSelect: "none",
    transition: "transform .08s ease",
  };

  const exportBtnHoverStyle = {
    boxShadow: "0 6px 14px rgba(97, 74, 158, 0.15)",
    backgroundColor: "#614a9e",
    color: "white",
  };

  const iconStyle = {
    width: 18,
    height: 18,
    display: "grid",
    placeItems: "center",
    borderRadius: 8,
    background:
      "linear-gradient(135deg, var(--icon-gradient-start), var(--icon-gradient-end))",
    color: "var(--color-text-button-action)",
    fontSize: 12,
    lineHeight: 1,
    flex: "0 0 auto",
  };

  return (
    <LoadingBlock loading={loading} label={loadingLabel}>
      <div style={wrapStyle}>
        {!exportMode ? (
          <ExportButton
            onClick={handleExportPNG}
            style={exportBtnStyle}
            hoverStyle={exportBtnHoverStyle}
            iconStyle={iconStyle}
          />
        ) : null}

        <HighchartsReact
          key={chartKey}
          ref={chartRef}
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: { height: exportMode ? 560 : CHART_H } }}
        />
      </div>
    </LoadingBlock>
  );
}

function ExportButton({ onClick, style, hoverStyle, iconStyle }) {
  const [hover, setHover] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...style, ...(hover ? hoverStyle : {}) }}
      title="Exportar PNG"
    >
      <span style={iconStyle}>⭳</span>
      Exportar PNG
    </button>
  );
}
